import { useEffect } from "react";
import { useLanguage, languages, stripLangFromPath, buildLocalizedPath, DEFAULT_LANG } from "../i18n/LanguageContext";

const SITE_URL = "https://aligners.academy";

// hreflang locale codes for Google
const LOCALE_MAP = {
  sq: "sq-AL",
  en: "en",
  it: "it-IT",
  de: "de-DE",
  fr: "fr-FR",
  mk: "mk-MK",
  el: "el-GR",
};

const useSEO = ({ title, description, keywords, ogTitle, ogDescription, ogImage, canonical, type = "website", schema = null }) => {
  const { lang } = useLanguage();

  useEffect(() => {
    const suffix = " | Albanian Aligners Academy";
    document.title = title ? title + suffix : "Albanian Aligners Academy | Kurse dhe Trajnime per Dentiste";

    // Set <html lang="..">
    document.documentElement.setAttribute("lang", LOCALE_MAP[lang] || lang);

    const setMeta = (name, content, property) => {
      if (!content) return;
      const attr = property ? "property" : "name";
      const val = property || name;
      let el = document.querySelector(`meta[${attr}="${val}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, val);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);
    setMeta(null, ogTitle || title, "og:title");
    setMeta(null, ogDescription || description, "og:description");
    setMeta(null, type, "og:type");
    setMeta(null, LOCALE_MAP[lang] || "en", "og:locale");
    if (ogImage) setMeta(null, ogImage, "og:image");
    setMeta("twitter:card", "summary_large_image");
    setMeta(null, ogTitle || title, "twitter:title");
    setMeta(null, ogDescription || description, "twitter:description");
    if (ogImage) setMeta(null, ogImage, "twitter:image");

    // ---- Canonical & hreflang ----
    const currentPath = stripLangFromPath(window.location.pathname);
    const canonicalUrl = canonical || `${SITE_URL}${buildLocalizedPath(currentPath, lang)}`;

    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute("href", canonicalUrl);

    // Remove existing hreflang
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());

    // Add hreflang for each language
    languages.forEach((l) => {
      const altPath = buildLocalizedPath(currentPath, l.code);
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", LOCALE_MAP[l.code] || l.code);
      link.setAttribute("href", `${SITE_URL}${altPath}`);
      document.head.appendChild(link);
    });
    // x-default points to Albanian
    const xDefault = document.createElement("link");
    xDefault.setAttribute("rel", "alternate");
    xDefault.setAttribute("hreflang", "x-default");
    xDefault.setAttribute("href", `${SITE_URL}${buildLocalizedPath(currentPath, DEFAULT_LANG)}`);
    document.head.appendChild(xDefault);

    // ---- Schema.org JSON-LD ----
    let schemaEl = document.querySelector('script[type="application/ld+json"][data-seo="page"]');
    if (schemaEl) schemaEl.remove();
    if (schema) {
      const s = document.createElement("script");
      s.setAttribute("type", "application/ld+json");
      s.setAttribute("data-seo", "page");
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
    }
  }, [title, description, keywords, ogTitle, ogDescription, ogImage, canonical, type, schema, lang]);
};

export default useSEO;
